import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ProtokollFilter, ProtokollSeite } from '@mcikm-vote/shared';

import { apiAnfrage } from '../api/anfrage';
import type { ApiFehler } from '../api/anfrage';

/**
 * Anbindung des administrativen Änderungsprotokolls (§29.3).
 *
 * Ausschließlich lesend — das Protokoll ist nicht bearbeitbar und nicht einzeln löschbar. Die
 * Downloads laufen wie bei Backups und Exporten über einen gewöhnlichen Verweis, nicht über
 * `fetch`: Das Speichern von Dateien kann der Browser besser als jede Nachbildung.
 */

const PROTOKOLL = '/api/v1/admin/protokoll';

export const PROTOKOLL_SCHLUESSEL = ['admin', 'protokoll'] as const;

/** Baut die Abfragezeichenkette aus Filter und Blätterung. */
export function protokollAbfrage(
  filter: ProtokollFilter,
  seite?: number,
  proSeite?: number,
): string {
  const parameter = new URLSearchParams();

  if (filter.von !== undefined) parameter.set('von', filter.von);
  if (filter.bis !== undefined) parameter.set('bis', filter.bis);
  if (filter.veranstaltungId !== undefined)
    parameter.set('veranstaltungId', filter.veranstaltungId);
  if (filter.abstimmungId !== undefined) parameter.set('abstimmungId', filter.abstimmungId);
  if (filter.vorgangsart !== undefined) parameter.set('vorgangsart', filter.vorgangsart);
  if (filter.erfolg !== undefined) parameter.set('erfolg', String(filter.erfolg));
  if (filter.herkunft !== undefined) parameter.set('herkunft', filter.herkunft);
  if (seite !== undefined) parameter.set('seite', String(seite));
  if (proSeite !== undefined) parameter.set('proSeite', String(proSeite));

  const text = parameter.toString();
  return text === '' ? '' : `?${text}`;
}

/** Pfad zum Herunterladen eines Protokollexports. Für `<a href>`; kein `fetch`. */
export function protokollExportPfad(format: 'csv' | 'pdf', filter: ProtokollFilter): string {
  return `${PROTOKOLL}/export.${format}${protokollAbfrage(filter)}`;
}

/** Eine gefilterte Seite des Protokolls (§29.3). */
export function useProtokoll(
  filter: ProtokollFilter,
  seite: number,
  proSeite: number,
): UseQueryResult<ProtokollSeite, ApiFehler> {
  const abfrage = protokollAbfrage(filter, seite, proSeite);

  return useQuery<ProtokollSeite, ApiFehler>({
    queryKey: [...PROTOKOLL_SCHLUESSEL, abfrage],
    queryFn: () => apiAnfrage<ProtokollSeite>(`${PROTOKOLL}${abfrage}`),
    // Beim Blättern nicht auf einen leeren Zustand zurückfallen — sonst springt die Tabelle.
    placeholderData: (vorher) => vorher,
  });
}
